import { ConsumptionMetaDataProps } from 'src/components/consumptionPage/ConsumptionMetaData';
import {
  CastType,
  Crew
} from 'src/components/consumptionPage/ConsumptionMetaData/ContentCrew';
import { TagProps, TagTypes } from 'src/components/Tags/types';
import { ContentTypes } from 'src/components/thumbnail/types';
import {
  SimilarContent,
  TvShowRelatedContent
} from 'src/services/GraphQL/Queries/types';
import { colors } from 'src/styles/colors';
import { ContentLiteData } from 'src/types/consumption';
import { globalUrlHelper } from 'src/utils/common';
import { DynamicObject } from 'src/utils/commonTypes';
import { NavMenuLinks } from 'src/utils/constants/RouterConstants';
import {
  CELEB_PROFILE_URL,
  ConsumptionPageUrls,
  MOVIE_BY_YEAR_URL
} from 'src/utils/constants/stringConstants';
import { getEpisodeReleaseDate } from 'src/utils/ConsumptionHelper/ConsumptionHelper';
import { convertTimeToHumanReadableFormat } from 'src/utils/convertTimeToHumanReadableFormatUtil';
import { checkKidsCard, titleGenerator } from 'src/utils/routeHandler';

import {
  MIXPANEL_MOVIES,
  MIXPANEL_TV_SHOWS,
  MIXPANEL_WEB_SERIES
} from '../../../utils/analytics/mixpanel/mixpanelConstants';
import {
  LANGUAGE_LABELS,
  LanguagesLabel
} from '../../../utils/languageConstants';

enum AssestSubType {
  MOVIE = 'movie',
  EPISODE = 'episode',
  TVSHOW = 'tvshow',
  WEBSERIES = 'original',
  SPORTS = 'sports_vod'
}

const generateCeleProfileUrl = (celebName: string) => {
  const formattedCelebName = celebName.replace(/\s+/g, '-').toLowerCase();
  return `${CELEB_PROFILE_URL}/${formattedCelebName}/filmography`;
};

export const generateMetaDataProps = (
  assetType: ContentTypes,
  contentLiteData: ContentLiteData,
  translations: DynamicObject,
  assetSubType?: AssestSubType,
  similarContents?: SimilarContent | TvShowRelatedContent,
  pathName?: string
): ConsumptionMetaDataProps => {
  const translationsData = {
    ...translations?.consumption,
    ...translations?.common
  };
  const isKidsData = checkKidsCard(contentLiteData?.genres);
  const mapB2BSubTypeWithContentType = () => {
    if (
      assetType === ContentTypes.B2B &&
      assetSubType === AssestSubType.MOVIE
    ) {
      return ContentTypes.movies;
    } else if (
      assetType === ContentTypes.B2B &&
      contentLiteData?.showDetails?.asset_subtype === AssestSubType.TVSHOW &&
      assetSubType === AssestSubType.EPISODE
    ) {
      return ContentTypes.tvshows;
    } else if (
      assetType === ContentTypes.B2B &&
      contentLiteData?.showDetails?.asset_subtype === AssestSubType.WEBSERIES &&
      assetSubType === AssestSubType.EPISODE
    ) {
      return ContentTypes.webSeries;
    } else if (
      assetType === ContentTypes.B2B &&
      contentLiteData?.asset_type === 9
    ) {
      return ContentTypes.livetv;
    } else if (
      assetType === ContentTypes.B2B &&
      assetSubType === AssestSubType.SPORTS
    ) {
      return ContentTypes.sports;
    } else if (
      assetType === ContentTypes.B2B &&
      contentLiteData?.asset_subtype === ContentTypes.video
    ) {
      return ContentTypes.video;
    }

    return assetType;
  };

  const contentType = mapB2BSubTypeWithContentType();

  const generatePrimaryTitle = () => {
    const title = contentLiteData?.title || '';
    switch (contentType) {
      case ContentTypes.movies: {
        return [
          {
            text: title
          }
        ];
      }
      case ContentTypes.tvshows: {
        const releaseData = contentLiteData?.release_date
          ? `${getEpisodeReleaseDate(String(contentLiteData.release_date))} • `
          : '';
        const episodeNumber = contentLiteData?.episodeNumber
          ? `Episode ${contentLiteData.episodeNumber} : `
          : '';
        return [
          {
            text: releaseData + episodeNumber + title
          }
        ];
      }
      case ContentTypes.webSeries: {
        /* If content is trailer or promo then id and title will comes under
        tvshow_details object otherwise we will read it from showDetails */
        const showTitle = titleGenerator(
          contentLiteData?.showDetails?.original_title ||
            contentLiteData?.showDetails?.title ||
            (typeof contentLiteData?.tvshow_details === 'object' &&
              (contentLiteData?.tvshow_details?.original_title ||
                contentLiteData?.tvshow_details?.title)) ||
            ''
        );
        const showId =
          contentLiteData?.showDetails?.id ||
          (typeof contentLiteData?.tvshow_details === 'object' &&
            contentLiteData?.tvshow_details?.id);
        const seasonId =
          contentLiteData?.seasonId ||
          (contentLiteData as DynamicObject)?.season ||
          '';
        const episodeNumber =
          contentLiteData?.episodeNumber || contentLiteData?.orderid;
        const episodeOrder = episodeNumber ? `E${episodeNumber} : ` : '';
        const seasonDetails =
          contentLiteData?.season_details ||
          contentLiteData?.showDetails?.seasons;
        const seasonNumber =
          seasonDetails?.find((item: DynamicObject) => item.id === seasonId)
            ?.orderid || 1;
        const seasonLink = contentLiteData
          ? `/${
              ConsumptionPageUrls.ZEE5ORIGINALSCONSUMPTION
            }/${showTitle}/${showId}${
              seasonNumber > 1 ? `/season-${seasonNumber}` : ''
            }/episodes`
          : '';
        return [
          {
            text: `S${seasonNumber} `,
            link: assetType === ContentTypes.B2B ? '' : seasonLink
          },
          {
            text: episodeOrder + title
          }
        ];
      }
      case ContentTypes.sports: {
        return [
          {
            text: title
          }
        ];
      }
      default:
        return [
          {
            text: title
          }
        ];
    }
  };

  const generateTags = (): TagProps[] => {
    const episodeReleaseData = contentLiteData?.release_date
      ? getEpisodeReleaseDate(String(contentLiteData.release_date))
      : '';
    const ageRating =
      contentLiteData?.age_rating_new || contentLiteData?.age_rating;
    const baseStyleObject = {
      tagType: TagTypes.TEXT,
      fontSize: 14,
      textColor: colors.greyText,
      fontWeight: 500
    };
    switch (contentType) {
      case ContentTypes.movies: {
        return [
          {
            tagType: TagTypes.SOLID,
            spaceX: '8',
            spaceY: '4',
            text: ageRating,
            textColor: colors.white,
            fontWeight: 500
          },
          {
            ...baseStyleObject,
            text: convertTimeToHumanReadableFormat(contentLiteData?.duration),
            showDot: true
          },
          {
            ...baseStyleObject,
            tagType:
              assetType === ContentTypes.B2B ? TagTypes.TEXT : TagTypes.LINK,
            text: `${new Date(contentLiteData?.release_date).getFullYear()}`,
            link: globalUrlHelper(
              `${MOVIE_BY_YEAR_URL}/${new Date(
                contentLiteData?.release_date
              ).getFullYear()}`
            ),
            textColor:
              assetType === ContentTypes.B2B
                ? colors.white
                : colors.PurpleLight,
            showDot: true
          },
          {
            tagType:
              assetType === ContentTypes.B2B ? TagTypes.TEXT : TagTypes.LINK,
            fontSize: 14,
            textColor:
              assetType === ContentTypes.B2B
                ? colors.white
                : colors.PurpleLight,
            text: isKidsData ? translationsData.KIDS : translationsData.MOVIES,
            link: isKidsData ? NavMenuLinks.KIDS : NavMenuLinks.MOVIES,
            showDot: true,
            mixpanelElementName: MIXPANEL_MOVIES
          }
        ];
      }
      case ContentTypes.tvshows: {
        const tvShowName =
          contentLiteData?.showDetails?.title ||
          contentLiteData?.showDetails?.original_title ||
          (typeof contentLiteData?.tvshow_details === 'object' &&
            (contentLiteData?.tvshow_details?.title ||
              contentLiteData?.tvshow_details?.original_title)) ||
          '';
        const tvShowId =
          contentLiteData?.showDetails?.id ||
          (typeof contentLiteData?.tvshow_details === 'object' &&
            contentLiteData?.tvshow_details?.id);
        const partnerName =
          contentLiteData?.content_partner_details?.content_partner_name;
        const tvShowLink = contentLiteData
          ? `${partnerName ? `/add-ons/` + partnerName : ''}/${
              ConsumptionPageUrls.TVSHOWS_CONSUMPTION
            }/${titleGenerator(tvShowName)}/${tvShowId}`
          : '';
        return [
          {
            tagType:
              assetType === ContentTypes.B2B ? TagTypes.TEXT : TagTypes.LINK,
            spaceX: '8',
            spaceY: '4',
            text: tvShowName,
            textColor: colors.white,
            link: tvShowLink,
            fontSize: 16,
            fontWeight: 500,
            h1tag: true
          },
          {
            tagType: TagTypes.SOLID,
            spaceX: '8',
            spaceY: '4',
            text: ageRating,
            textColor: colors.white,
            fontWeight: 500
          },
          {
            ...baseStyleObject,
            text: convertTimeToHumanReadableFormat(contentLiteData?.duration)
          },
          {
            tagType:
              assetType === ContentTypes.B2B ? TagTypes.TEXT : TagTypes.LINK,
            fontSize: 14,
            text: isKidsData
              ? translationsData.KIDS
              : translationsData.TV_SHOWS,
            link: isKidsData ? NavMenuLinks.KIDS : NavMenuLinks.TV_SHOWS,
            textColor: colors.PurpleLight,
            showDot: true,
            mixpanelElementName: MIXPANEL_TV_SHOWS
          }
        ];
      }
      case ContentTypes.webSeries: {
        const showTitle =
          contentLiteData?.showDetails?.title ||
          contentLiteData?.showDetails?.original_title ||
          (typeof contentLiteData?.tvshow_details === 'object' &&
            (contentLiteData?.tvshow_details?.title ||
              contentLiteData?.tvshow_details?.original_title));
        const showId =
          contentLiteData?.showDetails?.id ||
          (typeof contentLiteData?.tvshow_details === 'object' &&
            contentLiteData?.tvshow_details?.id);
        const tags: TagProps[] = [
          {
            tagType:
              assetType === ContentTypes.B2B ? TagTypes.TEXT : TagTypes.LINK,
            spaceX: '8',
            spaceY: '4',
            text: showTitle,
            link: contentLiteData
              ? `/${
                  ConsumptionPageUrls.ZEE5ORIGINALSCONSUMPTION
                }/${titleGenerator(showTitle)}/${showId}`
              : '',
            textColor: colors.white,
            fontSize: 16,
            fontWeight: 500
          },
          {
            tagType: TagTypes.SOLID,
            spaceX: '8',
            spaceY: '4',
            text: ageRating,
            textColor: colors.white,
            fontWeight: 500
          },
          {
            ...baseStyleObject,
            text: convertTimeToHumanReadableFormat(contentLiteData?.duration)
          }
        ];
        if (episodeReleaseData) {
          tags.push({
            ...baseStyleObject,
            text: episodeReleaseData,
            showDot: true
          });
        }
        tags.push({
          tagType:
            assetType === ContentTypes.B2B ? TagTypes.TEXT : TagTypes.LINK,
          fontSize: 14,
          textColor: colors.PurpleLight,
          text: isKidsData ? translationsData.KIDS : translationsData.WEBSERIES,
          link: isKidsData ? NavMenuLinks.KIDS : NavMenuLinks.WEBSERIES,
          showDot: true,
          mixpanelElementName: MIXPANEL_WEB_SERIES
        });
        return tags;
      }
      case ContentTypes.sports: {
        const tags: TagProps[] = [
          {
            tagType: TagTypes.TEXT,
            spaceX: '8',
            spaceY: '4',
            text: contentLiteData?.showDetails?.original_title,
            textColor: colors.white,
            fontSize: 16,
            fontWeight: 500
          },
          {
            tagType: TagTypes.SOLID,
            spaceX: '8',
            spaceY: '4',
            text:
              contentLiteData?.age_rating_new || contentLiteData?.age_rating,
            textColor: colors.white,
            fontWeight: 500
          },
          {
            ...baseStyleObject,
            text: convertTimeToHumanReadableFormat(contentLiteData?.duration)
          }
        ];
        if (episodeReleaseData) {
          tags.push({
            ...baseStyleObject,
            text: episodeReleaseData,
            showDot: true
          });
        }
        tags.push({
          tagType:
            assetType === ContentTypes.B2B ? TagTypes.TEXT : TagTypes.LINK,
          fontSize: 14,
          textColor: colors.PurpleLight,
          text: 'Sports',
          link: '/sports/games',
          showDot: true
        });
        return tags;
      }
      case ContentTypes.livetv: {
        return [];
      }
      case ContentTypes.video: {
        return [
          {
            tagType: TagTypes.SOLID,
            spaceX: '8',
            spaceY: '4',
            text:
              contentLiteData?.age_rating_new || contentLiteData?.age_rating,
            textColor: colors.white,
            fontWeight: 500
          },
          {
            ...baseStyleObject,
            text: convertTimeToHumanReadableFormat(contentLiteData?.duration)
          },
          {
            ...baseStyleObject,
            text: getEpisodeReleaseDate(`${contentLiteData?.release_date}`),
            showDot: true
          },
          {
            tagType:
              assetType === ContentTypes.B2B ? TagTypes.TEXT : TagTypes.LINK,
            fontSize: 14,
            textColor: colors.PurpleLight,
            text: 'Videos',
            link: '/videos',
            showDot: true
          }
        ];
      }
      //TODO: Missing case for Live-tv. Add after confirming from live site.
      default: {
        return [
          {
            tagType: TagTypes.LINK,
            fontSize: 14,
            text: '',
            link: ''
          }
        ];
      }
    }
  };

  const generateCrewObject = () => {
    let directors: Crew[] = [];
    if (
      contentLiteData?.directors ||
      contentLiteData?.extended?.music_directors
    ) {
      const movieDirectors = contentLiteData?.directors || [];
      const musicDirectors = contentLiteData?.extended?.music_directors || [];
      directors = [...movieDirectors, ...musicDirectors].map((director) => {
        return {
          castType: CastType.DIRECTOR,
          realName: director,
          reelName: translationsData?.DIRECTOR || '',
          textColor: colors.white
        };
      });
    }
    const actors = contentLiteData?.actors
      ? contentLiteData.actors
          .filter((actor) => {
            const [realName, reelName] = actor.split(':');
            return realName && reelName;
          })
          .map((actor) => {
            const [realName, reelName] = actor.split(':');
            if (assetType !== ContentTypes.B2B) {
              return {
                castType: CastType.ACTOR,
                realName,
                reelName,
                textColor: colors.white,
                link:
                  contentType === ContentTypes.movies
                    ? generateCeleProfileUrl(realName)
                    : null
              };
            }
            return {
              castType: CastType.ACTOR,
              realName,
              reelName,
              textColor: colors.white
            };
          })
      : [];
    return [...directors, ...actors];
  };

  const generateAudioLanguages = () => {
    return contentLiteData?.audio_languages?.map((audio) => {
      if (assetType !== ContentTypes.B2B) {
        return {
          text: translationsData?.LANGUAGES?.[audio],
          link: `/${contentType}/lang/${LANGUAGE_LABELS.en[
            audio.toLowerCase() as keyof LanguagesLabel
          ]?.toLowerCase()}`,
          textColor: colors.PurpleLight
        };
      }
      return {
        text: translationsData?.LANGUAGES?.[audio],
        textColor: colors.white
      };
    });
  };
  const generateSubTitles = () => {
    return contentLiteData?.subtitle_languages?.map((subTitle) => {
      return { text: translationsData?.LANGUAGES?.[subTitle] };
    });
  };
  const generateGenres = () => {
    return contentLiteData?.genres?.map(({ value, id }) => {
      if (assetType !== ContentTypes.B2B) {
        return {
          text: value,
          link: `/${contentType}/genre/${id
            .toLowerCase()
            .replace(/ /g, '-')
            .replace('/', '-or-')}`,
          textColor: colors.PurpleLight,
          mixpanelElementName: id
        };
      }
      return {
        text: value,
        textColor: colors.white
      };
    });
  };

  return {
    titleDetails: generatePrimaryTitle(),
    subTitle: contentType !== ContentTypes.movies ? contentLiteData?.title : '',
    tags: generateTags(),
    contentType: assetType,
    crew: generateCrewObject() as Crew[],
    description: contentLiteData?.description,
    assetInformation: {
      title: contentLiteData?.title,
      description: contentLiteData?.description,
      assetSubType: contentLiteData?.asset_subtype,
      assetType: contentLiteData?.asset_type,
      businessType: contentLiteData?.business_type,
      duration: contentLiteData?.duration,
      id: contentLiteData?.id,
      slug: `${pathName}`,
      releaseDate: `${contentLiteData?.release_date}`
    },
    audioLanguages: generateAudioLanguages(),
    subtitles: generateSubTitles(),
    genre: generateGenres(),
    trailerData: !similarContents
      ? null
      : contentType === ContentTypes.movies
      ? similarContents?.relatedVideos?.contents?.[0]
      : (similarContents as TvShowRelatedContent)?.seasons?.[0]?.trailers?.[0]
  };
};
